import { NotificationType, Notification, AiImportEntity, TaskEntity, EntityName, BidDecisionEntity, MarginDecisionEntity, ChatMessageEntity } from "@/graphql/__generated__/graphql";
import { TFunction } from "i18next";
import { NavigateFunction } from "react-router-dom";

/**
 * Interface representing a processed notification ready for display
 */
export type ProcessedNotication = {
  /** The title of the notification */
  title: string;
  /** The message body of the notification */
  message: string;
  /** Optional ID of the related tender */
  tenderID?: number;
  /** ID of the entity related to this notification */
  relatedID: number;
};

/**
 * Configuration for a notification type
 */
interface NotificationConfig {
  /** Key for retrieving the notification title from translation files */
  titleKey: string | ((_notification: Notification, _entityData: any) => string);
  /** Key for retrieving the notification message from translation files */
  messageKey: string | ((_notification: Notification, _entityData: any) => string);
  /** Function to build a URL for navigating when the notification is clicked */
  urlBuilder: (_notification: Notification, _entityData: any) => string;
}

/**
 * Configuration map for all notification types
 * Each notification type has its own configuration for title, message, and URL generation
 */
const NOTIFICATION_CONFIGS: Record<NotificationType, NotificationConfig> = {
  [NotificationType.BidDecisionRequired]: {
    titleKey: "biddecision.title",
    messageKey: (_notification: Notification, entityData: any) =>
      entityData.isOperationalOrg ? "biddecision.operationalorgmessage" : "biddecision.otherorgmessage",
    urlBuilder: (notification, { tenderId, entityId }) =>
      `/tender/${tenderId}/biddecisions/${entityId}`
  },
  [NotificationType.MarginDecisionRequired]: {
    titleKey: "margindecision.title",
    messageKey: (_notification: Notification, entityData: any) =>
      entityData.isOperationalOrg ? "margindecision.operationalorgmessage" : "margindecision.otherorgmessage",
    urlBuilder: (notification, { tenderId, entityId }) =>
      `/tender/${tenderId}/margindecisions/${entityId}`
  },
  [NotificationType.MarginRejected]: {
    titleKey: "marginrejected.title",
    messageKey: "marginrejected.message",
    urlBuilder: (notification, { tenderId, groupId }) =>
      `/tender/${tenderId}/margindecisions/${groupId}`
  },
  [NotificationType.AiImportSuccessful]: {
    titleKey: "aiimport.title",
    messageKey: "aiimport.message",
    urlBuilder: (notification, { entityId }) =>
      `/tender/new?importID=${entityId}`
  },
  [NotificationType.AssigneesOnTaskUpdated]: {
    titleKey: "usersassignedtotask.title",
    messageKey: "usersassignedtotask.message",
    urlBuilder: (notification, { tenderId }) =>
      `/tender/${tenderId}/tasksboard`
  },
  [NotificationType.TaskCompleted]: {
    titleKey: "taskcompleted.title",
    messageKey: "taskcompleted.message",
    urlBuilder: (notification, { tenderId }) =>
      `/tender/${tenderId}/tasksboard`
  },
  [NotificationType.ChatMentioned]: {
    titleKey: (_notification: Notification, entityData: any) => {
      if (entityData.entityName === EntityName.BidDecision) {
        return "chatmention.biddecisiongrouptitle";
      }
      if (entityData.entityName === EntityName.MarginDecision) {
        return "chatmention.margindecisiongrouptitle";
      }
      return "chatmention.tendertitle";
    },
    messageKey: "chatmention.message",
    urlBuilder: (notification, { tenderId, groupId, entityName }) => {
      if (entityName === EntityName.BidDecision) {
        return `/tender/${tenderId}/biddecisions/${groupId}`;
      }
      if (entityName === EntityName.MarginDecision) {
        return `/tender/${tenderId}/margindecisions/${groupId}`;
      }
      return `/tender/${tenderId}/chat`;
    }
  },
  [NotificationType.AllBidDecisionsCompleted]: {
    titleKey: "allbiddecisionscompleted.title",
    messageKey: "allbiddecisionscompleted.message",
    urlBuilder: (notification, { tenderId }) =>
      `/tender/${tenderId}/details?openAccordion=item-0`
  },
  [NotificationType.AllMarginDecisionsCompleted]: {
    titleKey: "allmargindecisionscompleted.title",
    messageKey: "allmargindecisionscompleted.message",
    urlBuilder: (notification, { tenderId }) =>
      `/tender/${tenderId}/details?openAccordion=item-2`
  },
  [NotificationType.BidDecisionGroupCompleted]: {
    titleKey: "biddecisiongroupcompleted.title",
    messageKey: "biddecisiongroupcompleted.message",
    urlBuilder: (notification, { tenderId, entityId }) =>
      `/tender/${tenderId}/biddecisions/${entityId}`
  },
  [NotificationType.MarginDecisionGroupCompleted]: {
    titleKey: "margindecisiongroupcompleted.title",
    messageKey: "margindecisiongroupcompleted.message",
    urlBuilder: (notification, { tenderId, entityId }) =>
      `/tender/${tenderId}/margindecisions/${entityId}`
  },
  [NotificationType.MarginDecisionParticipantAdded]: {
    titleKey: "margindecisionparticipantadded.title",
    messageKey: "margindecisionparticipantadded.message",
    urlBuilder: (notification, { tenderId, entityId }) =>
      `/tender/${tenderId}/margindecisions/${entityId}`
  },
  [NotificationType.MarginDecisionParticipantRemoved]: {
    titleKey: "margindecisionparticipantremoved.title",
    messageKey: "margindecisionparticipantremoved.message",
    urlBuilder: (notification, { tenderId, entityId }) =>
      `/tender/${tenderId}/margindecisions/${entityId}`
  },
  [NotificationType.MarginRequestAutoApproved]: {
    titleKey: "marginrequestautoapproved.title",
    messageKey: "marginrequestautoapproved.message",
    urlBuilder: (notification, { tenderId, entityId }) =>
      `/tender/${tenderId}/margindecisions/${entityId}`
  },
  [NotificationType.SheetCreated]: {
    titleKey: "sheetcreated.title",
    messageKey: "sheetcreated.message",
    urlBuilder: (notification, { tenderId }) =>
      `/tender/${tenderId}`
  },
  [NotificationType.TenderCreated]: {
    titleKey: "tendercreated.title",
    messageKey: "tendercreated.message",
    urlBuilder: (notification, { tenderId }) =>
      `/tender/${tenderId}`
  },
  [NotificationType.BidDecisionSubmitted]: {
    titleKey: "biddecisionsubmitted.title",
    messageKey: "biddecisionsubmitted.message",
    urlBuilder: (notification, { tenderId, entityId }) =>
      `/tender/${tenderId}/biddecisions/${entityId}`
  },
  [NotificationType.MarginDecisionsRequested]: {
    titleKey: "margindecisionsrequested.title",
    messageKey: "margindecisionsrequested.message",
    urlBuilder: (notification, { tenderId }) =>
      `/tender/${tenderId}/margindecisions`
  },
  [NotificationType.MarginDecisionOnGroupRequested]: {
    titleKey: "margindecisionongrouprequested.title",
    messageKey: "margindecisionongrouprequested.message",
    urlBuilder: (notification, { tenderId, groupId }) =>
      `/tender/${tenderId}/margindecisions/${groupId}`
  },
  [NotificationType.MarginDecisionSubmitted]: {
    titleKey: "margindecisionsubmitted.title",
    messageKey: "margindecisionsubmitted.message",
    urlBuilder: (notification, { tenderId, entityId }) =>
      `/tender/${tenderId}/margindecisions/${entityId}`
  },
  [NotificationType.NominatedAsChanceAdmin]: {
    titleKey: "nominatedaschanceadmin.title",
    messageKey: "nominatedaschanceadmin.message",
    urlBuilder: (notification, { tenderId }) =>
      `/tender/${tenderId}`
  },
  [NotificationType.UserInvited]: {
    titleKey: "userinvited.title",
    messageKey: "userinvited.message",
    urlBuilder: (_notification, { _ }) =>
      "/dashboard"
  }
};

/** Returns true when notification.entity is present. */
export const hasNotificationEntity = (notification: {
  entity?: unknown | null;
}): boolean => notification.entity != null;

/**
 * Entity-specific data extractors
 * Each handler extracts relevant data from a specific entity type
 */
const ENTITY_HANDLERS = {
  [EntityName.BidDecision]: (entity: BidDecisionEntity | null | undefined) => ({
    tenderName: entity?.group?.tenderName,
    groupId: entity?.group?.groupId,
    tenderId: entity?.group?.tenderId
  }),
  [EntityName.MarginDecision]: (entity: MarginDecisionEntity | null | undefined) => ({
    tenderName: entity?.group?.tenderName,
    groupId: entity?.group?.groupId,
    tenderId: entity?.group?.tenderId
  }),
  [EntityName.ChatMessage]: (entity: ChatMessageEntity | null | undefined) => ({
    tenderName: entity?.messageEntity?.tenderName,
    tenderId: entity?.messageEntity?.tenderId
  }),
  [EntityName.Task]: (entity: TaskEntity | null | undefined) => ({
    taskName: entity?.taskName,
    tenderName: entity?.tenderName,
    tenderId: entity?.tenderId
  }),
  [EntityName.AiImport]: (entity: AiImportEntity | null | undefined) => ({
    fileName: entity?.fileName
  }),
  // Default handler for group entities and fallback
  default: (entity: any, orgId: number) => ({
    tenderId: entity?.tenderId,
    isOperationalOrg: entity?.operationalOrganizationId === orgId,
    orgShortName: entity?.operationalOrganizationShortName,
    tenderName: entity?.tenderName
  })
};

/**
 * Processes a notification into a displayable format
 *
 * @param t - Translation function from i18next
 * @param notification - The raw notification object
 * @param orgId - The organization ID of the current user
 * @returns A processed notification with title, message, and IDs
 */
export const processNotification = (t: TFunction, notification: Notification, orgId: number): ProcessedNotication => {
  const prefix = "translation:notificationservice.notifications";

  // Get configuration for this notification type
  const config = NOTIFICATION_CONFIGS[notification.type];
  if (!config) {
    throw new Error(`Unsupported notification type: ${notification.type}`);
  }

  if (!hasNotificationEntity(notification)) {
    const titleKey = typeof config.titleKey === "function"
      ? config.titleKey(notification, { entityName: notification.entityName })
      : config.titleKey;
    return {
      title: t(`${prefix}.${titleKey}`),
      message: "",
      relatedID: notification.entityId
    };
  }

  // Select the appropriate handler for this entity type or use default
  const handler = ENTITY_HANDLERS[notification.entityName] || ENTITY_HANDLERS.default;
  // Extract relevant data from the entity
  const entityData = handler(notification.entity, orgId);

  // Generate title using either a titleKey string or a function that returns a titleKey
  const titleKey = typeof config.titleKey === "function"
    ? config.titleKey(notification, { ...entityData, entityName: notification.entityName })
    : config.titleKey;

  // Generate messageKey using either a messageKey string or a function that returns a messageKey
  const messageKey = typeof config.messageKey === "function"
    ? config.messageKey(notification, { ...entityData, entityName: notification.entityName })
    : config.messageKey;

  // Generate title and message using translation keys and entity data
  const title = t(`${prefix}.${titleKey}`);
  const message = t(`${prefix}.${messageKey}`, entityData);

  return {
    title,
    message,
    tenderID: entityData.tenderId,
    relatedID: notification.entityId
  };
};

/**
 * Generates a URL for the notification and navigates to it
 *
 * @param notification - The notification to navigate to
 * @param navigate - React Router's navigate function
 */
export const getNotificationURL = (notification: Notification, navigate: NavigateFunction): void => {
  if (!hasNotificationEntity(notification)) {
    return;
  }

  // Get entity data using the appropriate handler
  const handler = ENTITY_HANDLERS[notification.entityName] || ENTITY_HANDLERS.default;
  const entityData = handler(notification.entity, null);

  // Get configuration for this notification type
  const config = NOTIFICATION_CONFIGS[notification.type];

  // Build the URL using the notification config's URL builder
  const url = config.urlBuilder(notification, {
    ...entityData,
    entityId: notification.entityId,
    entityName: notification.entityName
  });

  // Navigate to the constructed URL
  navigate(url);
};
